/**
 * Admin Dashboard - Refactored with modular components and hooks
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Settings, Tags, QrCode, Upload, Copy, ExternalLink, Plus, 
  RefreshCw, BarChart3, Trash2, PlusCircle, FolderOpen, 
  RotateCcw, LayoutGrid, List, ChevronDown, ChevronRight, 
  GripVertical, Search, X, Eye, Users, Palette, DollarSign,
  Mail, MessageSquare, CheckCircle, Archive, Clock, FileText, Download, Edit3, HelpCircle, Folder, FolderInput, Megaphone, FolderPlus, Bot, Globe, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Import custom hooks
import { useTagManagement, useTagFilters } from '../hooks';

// Import admin components
import { 
  ConfirmModal, 
  BulkActionBar, 
  StatusFilters,
  QuickSelectMenu,
  Pagination,
  TagCard,
  BatchList,
  UUIDGenerator,
  CustomTagForm,
  BulkUpload,
  QRStyleModal,
  AdminSettingsModal,
  BillingOverviewModal,
  TagDateModal,
  WelcomeGuide,
  ReportIssueModal,
  FolderSidebar,
  MoveToFolderMenu,
  StickerPrintModal
} from '../components/admin';

// Import FolderModal for mobile
import { FolderModal } from '../components/admin/FolderManager';

// Import Help Menu
import { HelpMenu } from '../components/InteractiveGuide';

// Import Campaign Manager
import CampaignManager from '../components/admin/CampaignManager';

// Import AI Assistant
import AdminAssistant from '../components/admin/AdminAssistant';

// Import version info
import { APP_VERSION, BUILD_DATE } from '../config/version';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab state - get from URL or default to 'tags'
  const activeTab = searchParams.get('tab') || 'tags';
  
  // Function to change tab (updates URL)
  const setActiveTab = (tabId) => {
    if (tabId === 'tags') {
      // Remove tab param for default tab to keep URL clean
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tabId);
    }
    setSearchParams(searchParams, { replace: false });
  };
  
  // Admin context (master vs tenant)
  const [adminContext, setAdminContext] = useState({ is_master: true, features: {} });
  
  // Use custom hooks
  const {
    tags, stats, loading, selectedTags, batches, analyticsStats, dailyViews, batchStats,
    setSelectedTags, loadTags, loadBatches, loadBatchStats, loadAnalyticsStats, loadDailyViews,
    resetTag, deleteTag, deleteMultipleTags, bulkResetTags, bulkMoveToFolder,
    downloadQRCode, bulkDownloadQRCodes, deleteBatch, toggleSelectTag, toggleSelectAll, copyToClipboard
  } = useTagManagement();
  
  // Sort and filter state (must be before useTagFilters)
  const [sortBy, setSortBy] = useState('created_desc');
  const [colorFilter, setColorFilter] = useState('all');

  const {
    searchQuery, filterBatch, statusFilter, folderFilter, currentPage, tagsPerPage, viewMode,
    getAllFolders, getFilteredTags, getPaginatedTags, totalPages, statusCounts, folderCounts, getTagsByFolder,
    getTagThumbnail, setSearchQuery, setFilterBatch, setStatusFilter, setFolderFilter, setCurrentPage, setTagsPerPage, setViewMode
  } = useTagFilters(tags, sortBy, colorFilter);
  
  // UI state
  const [hoveredTag, setHoveredTag] = useState(null);
  const [draggedTag, setDraggedTag] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [showAnalytics, setShowAnalytics] = useState(true);
  
  // Modal state
  const [resetConfirm, setResetConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkResetConfirm, setBulkResetConfirm] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [qrStyleModal, setQrStyleModal] = useState({ open: false, nfcCode: null });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [dateModal, setDateModal] = useState({ open: false, tag: null });
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showStickerModal, setShowStickerModal] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [unorganizedCount, setUnorganizedCount] = useState(0);
  const [moveMenuOpen, setMoveMenuOpen] = useState({ open: false, position: { top: 0, left: 0 } });
  const [showMobileFolderModal, setShowMobileFolderModal] = useState(false);

  // Contact submissions state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactFilter, setContactFilter] = useState('all');

  // Load contacts
  const loadContacts = async () => {
    setContactsLoading(true);
    try {
      const params = new URLSearchParams({ page: contactsPage, per_page: 20 });
      if (contactFilter !== 'all') params.append('status', contactFilter);
      const response = await axios.get(`${BACKEND_URL}/api/admin/contacts?${params}`);
      setContacts(response.data.submissions);
      setContactsTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      toast.error('Failed to load contact submissions');
    } finally {
      setContactsLoading(false);
    }
  };

  const updateContactStatus = async (contactId, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/contacts/${contactId}/status?status=${status}`);
      toast.success(`Marked as ${status}`);
      loadContacts();
    } catch (error) {
      console.error('Failed to update contact:', error);
      toast.error('Failed to update status');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/contacts/${contactId}`);
      toast.success('Contact deleted');
      loadContacts();
    } catch (error) {
      console.error('Failed to delete contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  // Load folders
  const loadFolders = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/folders`);
      setFolders(response.data.folders || []);
      setUnorganizedCount(response.data.unorganized_count || 0);
    } catch (error) {
      // Folders endpoint might not exist on older backends - gracefully handle
      console.error('Failed to load folders:', error);
      setFolders([]);
      setUnorganizedCount(0);
    }
  };

  // Create new folder
  const handleCreateFolder = async (name, color) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/folders`, { name, color });
      toast.success('Folder created!');
      setShowMobileFolderModal(false);
      loadFolders();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  // Move tags to folder
  const moveTagsToFolder = async (folderId) => {
    if (selectedTags.length === 0) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/tags/move`, {
        tag_ids: selectedTags,
        folder_id: folderId
      });
      toast.success(`Moved ${selectedTags.length} tag(s)`);
      setSelectedTags([]);
      loadTags();
      loadFolders();
    } catch (error) {
      toast.error('Failed to move tags');
    }
  };

  // Load admin context on mount
  useEffect(() => {
    const loadAdminContext = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/context`);
        const data = await response.json();
        setAdminContext(data);
      } catch (error) {
        console.error('Failed to load admin context:', error);
        // Default to showing all features if check fails
        setAdminContext({ is_master: true, features: { white_label: true, global_settings: true } });
      }
    };
    loadAdminContext();
  }, []);

  // Load data on mount and tab change
  useEffect(() => {
    loadAnalyticsStats();
    loadDailyViews();
    loadFolders();
    // Always load tags regardless of tab - they're needed for stats
    loadTags();
    loadBatches();
    if (activeTab === 'batches') {
      loadBatchStats();
    }
    if (activeTab === 'contacts') {
      loadContacts();
    }
  }, [activeTab, loadTags, loadBatches, loadBatchStats, loadAnalyticsStats, loadDailyViews]);

  // Reload contacts when filter or page changes
  useEffect(() => {
    if (activeTab === 'contacts') {
      loadContacts();
    }
  }, [contactsPage, contactFilter]);

  // Handlers
  const handleConfirmReset = async () => {
    if (resetConfirm) {
      await resetTag(resetConfirm);
      setResetConfirm(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm) {
      if (deleteConfirm.type === 'multiple') {
        await deleteMultipleTags(selectedTags);
      } else {
        await deleteTag(deleteConfirm.nfcCode);
      }
      setDeleteConfirm(null);
    }
  };

  const handleBulkReset = async () => {
    setBulkActionLoading(true);
    await bulkResetTags(selectedTags);
    setBulkActionLoading(false);
    setBulkResetConfirm(false);
  };

  const handleBulkMove = async (targetFolder) => {
    setBulkActionLoading(true);
    await bulkMoveToFolder(selectedTags, targetFolder);
    setBulkActionLoading(false);
  };

  const handleBulkDownloadQR = async () => {
    setBulkActionLoading(true);
    await bulkDownloadQRCodes(selectedTags);
    setBulkActionLoading(false);
  };

  const handleToggleQR = async (tag) => {
    try {
      const newQrEnabled = tag.qr_enabled === false ? true : false;
      await axios.post(`${BACKEND_URL}/api/admin/tags/qr-setting`, {
        nfc_codes: [tag.nfc_code],
        qr_enabled: newQrEnabled
      });
      toast.success(`QR code ${newQrEnabled ? 'enabled' : 'disabled'} for ${tag.nfc_code}`);
      loadTags(); // Refresh tags
    } catch (error) {
      toast.error('Failed to update QR setting');
    }
  };

  // Quick create a tag with one click
  const handleQuickCreate = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/generate-uuid`, {
        count: 1,
        prefix: '',
        qr_enabled: true
      });
      toast.success(`Tag created: ${response.data.uuids[0]}`);
      loadTags();
      // Copy to clipboard
      navigator.clipboard.writeText(`${window.location.origin}/${response.data.uuids[0]}/tag`);
      toast.success('Upload link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to create tag');
    }
  };

  // Export all data for migration
  const handleExportData = () => {
    // Direct link approach - works better on mobile
    toast.info('Starting download...');
    window.open(`${BACKEND_URL}/api/admin/export`, '_blank');
  };

  const handleDragStart = (e, tag) => {
    setDraggedTag(tag);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(folderId);
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    if (draggedTag) {
      // Use folder_id based move (null = unorganized)
      await bulkMoveToFolder([draggedTag.nfc_code], folderId);
      loadFolders(); // Refresh folder counts
    }
    setDraggedTag(null);
    setDragOverFolder(null);
  };

  // Set color label for a tag
  const setColorLabel = async (nfcCode, color) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/tags/color-label`, {
        nfc_code: nfcCode,
        color_label: color
      });
      toast.success(color ? `Label set to ${color}` : 'Label removed');
      loadTags(); // Refresh to show updated label
    } catch (error) {
      console.error('Failed to set color label:', error);
      toast.error('Failed to set color label');
    }
  };

  // Callback for when tags/batches are created
  const handleTagsCreated = () => {
    loadTags();
    loadBatches();
  };

  // Quick select handlers
  const selectAllFiltered = () => {
    setSelectedTags(getFilteredTags.map(t => t.nfc_code));
    toast.success(`Selected ${getFilteredTags.length} tags`);
  };

  const selectAllEmpty = () => {
    const emptyTags = tags.filter(t => !t.has_content && !t.message && (!t.media_files || t.media_files.length === 0));
    setSelectedTags(emptyTags.map(t => t.nfc_code));
    toast.success(`Selected ${emptyTags.length} empty tags`);
  };

  const selectAllWithContent = () => {
    const contentTags = tags.filter(t => t.has_content || t.message || (t.media_files && t.media_files.length > 0));
    setSelectedTags(contentTags.map(t => t.nfc_code));
    toast.success(`Selected ${contentTags.length} tags with content`);
  };

  const selectByBatch = (batchName) => {
    const batchTags = batchName === 'uncategorized' 
      ? tags.filter(t => !t.batch)
      : tags.filter(t => t.batch === batchName);
    setSelectedTags(batchTags.map(t => t.nfc_code));
    toast.success(`Selected ${batchTags.length} tags in "${batchName}"`);
  };

  const selectRecent = () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recentTags = tags.filter(t => new Date(t.created_at) >= sevenDaysAgo);
    setSelectedTags(recentTags.map(t => t.nfc_code));
    toast.success(`Selected ${recentTags.length} recent tags`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Reset Confirmation Modal */}
      <ConfirmModal
        isOpen={!!resetConfirm}
        onClose={() => setResetConfirm(null)}
        onConfirm={handleConfirmReset}
        title="Reset Tag?"
        description={`This will clear all content from: ${resetConfirm}`}
        warning="The tag will remain but all messages, media files, and notes will be deleted."
        type="reset"
        confirmText="Yes, Reset"
      />

      {/* Welcome Guide for first-time users */}
      <WelcomeGuide />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleConfirmDelete}
        title={deleteConfirm?.type === 'multiple' ? 'Delete Tags?' : 'Delete Tag?'}
        description={deleteConfirm?.type === 'multiple' 
          ? `This will permanently delete ${deleteConfirm?.count} tags.`
          : `This will permanently delete: ${deleteConfirm?.nfcCode}`}
        warning="This action cannot be undone. All content and files will be lost."
        type="delete"
        confirmText="Yes, Delete"
      />

      {/* Bulk Reset Confirmation Modal */}
      <ConfirmModal
        isOpen={bulkResetConfirm}
        onClose={() => setBulkResetConfirm(false)}
        onConfirm={handleBulkReset}
        title={`Reset ${selectedTags.length} Tags?`}
        description="This will clear all content from the selected tags. The tags will remain but all messages, media files, and notes will be deleted."
        warning="This action cannot be undone."
        type="reset"
        confirmText="Yes, Reset All"
        loading={bulkActionLoading}
      />

      {/* QR Code Style Modal */}
      <QRStyleModal
        isOpen={qrStyleModal.open}
        onClose={() => setQrStyleModal({ open: false, nfcCode: null })}
        nfcCode={qrStyleModal.nfcCode}
        onDownload={downloadQRCode}
      />

      {/* Admin Settings Modal */}
      <AdminSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Billing Overview Modal */}
      <BillingOverviewModal
        isOpen={showBillingModal}
        onClose={() => setShowBillingModal(false)}
      />

      {/* Tag Date Modal */}
      <TagDateModal
        isOpen={dateModal.open}
        onClose={() => setDateModal({ open: false, tag: null })}
        tag={dateModal.tag}
        onSuccess={loadTags}
      />

      {/* Report Issue Modal */}
      <ReportIssueModal
        isOpen={showReportIssueModal}
        onClose={() => setShowReportIssueModal(false)}
      />

      {/* Sticker Print Modal */}
      <StickerPrintModal
        isOpen={showStickerModal}
        onClose={() => setShowStickerModal(false)}
        selectedTags={selectedTags.length > 0 ? selectedTags.map(id => {
          const tag = tags.find(t => t.nfc_code === id || t._id === id);
          return tag ? tag.nfc_code : id;
        }) : tags.map(t => t.nfc_code)}
      />

      {/* Mobile Folder Creation Modal */}
      <FolderModal
        isOpen={showMobileFolderModal}
        onClose={() => setShowMobileFolderModal(false)}
        folder={null}
        onSave={handleCreateFolder}
      />

      {/* Floating Bulk Action Bar */}
      {selectedTags.length > 0 && activeTab === 'tags' && (
        <BulkActionBar
          selectedCount={selectedTags.length}
          selectedTags={selectedTags}
          folders={folders}
          onMove={moveTagsToFolder}
          onDownloadQR={handleBulkDownloadQR}
          onReset={() => setBulkResetConfirm(true)}
          onDelete={() => setDeleteConfirm({ type: 'multiple', count: selectedTags.length })}
          onClearSelection={() => setSelectedTags([])}
          onRefresh={() => { loadTags(); loadFolders(); }}
          loading={bulkActionLoading}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Header */}
          <div className="mb-8 sm:mb-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 sm:w-8 sm:h-8 text-primary" strokeWidth={1.5} />
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold font-heading tracking-tight">
                  Admin Dashboard
                </h1>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Quick Create Button */}
                <button
                  onClick={handleQuickCreate}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-xs sm:text-sm font-medium transition-all shadow-sm hover:shadow-md"
                  title="Instantly create a new tag"
                  data-testid="quick-create-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden xs:inline">Quick</span> Create
                </button>
                {/* Settings - only show for master admin */}
                {adminContext.features?.global_settings && (
                  <button
                    onClick={() => setShowSettingsModal(true)}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-xs sm:text-sm font-medium transition-all"
                    title="Admin Settings"
                    data-testid="admin-settings-btn"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Settings</span>
                  </button>
                )}
                {/* Export Data */}
                <button
                  onClick={handleExportData}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-xs sm:text-sm font-medium transition-all"
                  title="Export Data for Migration"
                  data-testid="export-data-btn"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
                {/* Print Stickers */}
                <button
                  onClick={() => setShowStickerModal(true)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 text-xs sm:text-sm font-medium transition-all"
                  title="Print QR Code Stickers"
                  data-testid="print-stickers-btn"
                >
                  <Printer className="w-4 h-4" />
                  <span className="hidden sm:inline">Print</span>
                </button>
                {/* Analytics Hub */}
                <button
                  onClick={() => navigate('/admin/analytics')}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-xs sm:text-sm font-medium transition-all"
                  title="Analytics Hub"
                  data-testid="analytics-hub-btn"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="hidden sm:inline">Analytics</span>
                </button>
                {/* Advanced Analytics */}
                <button
                  onClick={() => navigate('/admin/analytics/advanced')}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 text-xs sm:text-sm font-medium transition-all"
                  title="Advanced Analytics - Heat Maps, Live Counter, PDF Reports"
                  data-testid="advanced-analytics-btn"
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">Advanced</span>
                </button>
                {/* Report Issue */}
                <button
                  onClick={() => setShowReportIssueModal(true)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-red-100 hover:bg-red-200 text-red-700 text-xs sm:text-sm font-medium transition-all"
                  title="Report an Issue"
                  data-testid="report-issue-btn"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">Help</span>
                </button>
                {/* Billing - only show for master admin */}
                {adminContext.features?.white_label && (
                  <button
                    onClick={() => setShowBillingModal(true)}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-xs sm:text-sm font-medium transition-all"
                    title="Billing Overview"
                    data-testid="billing-btn"
                  >
                    <DollarSign className="w-4 h-4" />
                    <span className="hidden sm:inline">Billing</span>
                  </button>
                )}
                {/* White-Label - only show for master admin */}
                {adminContext.features?.white_label && (
                  <button
                    onClick={() => navigate('/admin/whitelabel')}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-xs sm:text-sm font-medium transition-all"
                    title="White-Label Setup"
                    data-testid="whitelabel-btn"
                  >
                    <Palette className="w-4 h-4" />
                    <span className="hidden sm:inline">White-Label</span>
                  </button>
                )}
                {/* AI Assistant - Prominent floating button */}
                {/* Content Editor */}
                <button
                  onClick={() => navigate('/admin/content')}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-xs sm:text-sm font-medium transition-all"
                  title="Edit Marketing Content"
                  data-testid="content-editor-btn"
                >
                  <Edit3 className="w-4 h-4" />
                  <span className="hidden sm:inline">Content</span>
                </button>
              </div>
            </div>
            <p className="text-base sm:text-lg text-muted-foreground">
              Manage your NFC tags, generate UUIDs, and create QR codes
            </p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="rounded-xl sm:rounded-2xl border border-border/40 bg-card/50 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Tags className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  <span className="text-xs sm:text-sm text-muted-foreground">Total Tags</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold mt-2">{stats.total_tags}</p>
              </div>
              <div className="rounded-xl sm:rounded-2xl border border-border/40 bg-card/50 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                  <span className="text-xs sm:text-sm text-muted-foreground">With Content</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold mt-2">{stats.tags_with_content}</p>
              </div>
              <div className="rounded-xl sm:rounded-2xl border border-border/40 bg-card/50 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                  <span className="text-xs sm:text-sm text-muted-foreground">Total Views</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold mt-2">{analyticsStats?.total_views?.toLocaleString() || 0}</p>
              </div>
              <div className="rounded-xl sm:rounded-2xl border border-border/40 bg-card/50 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                  <span className="text-xs sm:text-sm text-muted-foreground">Unique Visitors</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold mt-2">{analyticsStats?.total_unique_visitors?.toLocaleString() || 0}</p>
              </div>
              {/* Scan Source Breakdown */}
              {analyticsStats?.scan_sources && (
                <div className="rounded-xl sm:rounded-2xl border border-border/40 bg-card/50 p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3">
                    <QrCode className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                    <span className="text-xs sm:text-sm text-muted-foreground">Scan Sources</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">NFC Taps</span>
                      <span className="text-sm font-semibold">{analyticsStats.scan_sources.nfc?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">QR Scans</span>
                      <span className="text-sm font-semibold text-green-600">{analyticsStats.scan_sources.qr?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Shared Links</span>
                      <span className="text-sm font-semibold text-blue-600">{analyticsStats.scan_sources.link?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analytics Chart */}
          {dailyViews.length > 0 && (
            <div className="mb-8 rounded-2xl border border-border/40 bg-card/50 overflow-hidden">
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-secondary/20"
                onClick={() => setShowAnalytics(!showAnalytics)}
              >
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Views - Last 7 Days</h3>
                </div>
                <span className="text-sm text-muted-foreground">
                  {dailyViews.reduce((sum, d) => sum + d.views, 0)} total
                </span>
              </div>
              {showAnalytics && (
                <div className="px-4 pb-4">
                  <div className="flex items-end justify-between gap-2 h-24">
                    {dailyViews.map((day, idx) => {
                      const maxViews = Math.max(...dailyViews.map(d => d.views), 1);
                      const height = day.views > 0 ? (day.views / maxViews) * 100 : 4;
                      const isToday = idx === dailyViews.length - 1;
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-xs text-muted-foreground">{day.views}</span>
                          <div 
                            className={`w-full rounded-t-lg ${isToday ? 'bg-primary' : 'bg-primary/40'}`}
                            style={{ height: `${height}%`, minHeight: '4px' }}
                          />
                          <span className={`text-xs ${isToday ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                            {day.day}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex overflow-x-auto gap-2 mb-6 sm:mb-8 pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-hide">
            {[
              { id: 'tags', label: 'All Tags', shortLabel: 'Tags', icon: Tags },
              { id: 'batches', label: 'Batches', shortLabel: 'Batches', icon: FolderOpen },
              { id: 'campaigns', label: 'Campaigns', shortLabel: 'Campaigns', icon: Megaphone },
              // Only show Edit Content for master admin
              ...(adminContext.is_master ? [{ id: 'content', label: 'Edit Content', shortLabel: 'Content', icon: Edit3 }] : []),
              { id: 'contacts', label: 'Contacts', shortLabel: 'Contacts', icon: MessageSquare },
              { id: 'documents', label: 'Documents', shortLabel: 'Docs', icon: FileText },
              { id: 'generator', label: 'UUID Generator', shortLabel: 'Generate', icon: Plus },
              { id: 'custom', label: 'Custom Tag', shortLabel: 'Custom', icon: PlusCircle },
              { id: 'bulk', label: 'Bulk Upload', shortLabel: 'Bulk', icon: Upload },
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`${tab.id}-tab`}
                onClick={() => {
                  if (tab.id === 'content') {
                    navigate('/admin/content');
                  } else {
                    setActiveTab(tab.id);
                  }
                }}
                className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium transition-all whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground shadow-lg scale-105'
                    : 'bg-secondary/50 text-secondary-foreground hover:bg-secondary'
                }`}
              >
                <tab.icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="sm:hidden">{tab.shortLabel}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'tags' && (
            <div className="space-y-4 sm:space-y-6">
              {/* Sticky Header */}
              <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-xl -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 sm:py-4 border-b border-border/40">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto pb-1 sm:pb-0">
                    <h2 className="text-lg sm:text-xl font-bold shrink-0">Tags</h2>
                    <StatusFilters
                      activeFilter={statusFilter}
                      onFilterChange={setStatusFilter}
                      counts={statusCounts}
                    />
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    {/* Search */}
                    <div className="relative flex-1 sm:flex-initial">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        data-testid="search-input"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        className="bg-secondary rounded-full pl-9 pr-8 py-2 text-sm w-full sm:w-48 sm:focus:w-64 transition-all focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary/80 rounded-full">
                          <X className="w-3 h-3 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                    
                    {/* View Toggle */}
                    <div className="flex bg-secondary rounded-full p-1">
                      {[
                        { id: 'grid', icon: LayoutGrid },
                        { id: 'list', icon: List },
                        { id: 'folders', icon: FolderOpen },
                      ].map(v => (
                        <button
                          key={v.id}
                          data-testid={`${v.id}-view-btn`}
                          onClick={() => setViewMode(v.id)}
                          className={`p-1.5 sm:p-2 rounded-full transition-all ${viewMode === v.id ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary/80'}`}
                        >
                          <v.icon className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      ))}
                    </div>
                    
                    {/* Batch Filter */}
                    <select
                      value={filterBatch}
                      onChange={(e) => setFilterBatch(e.target.value)}
                      className="bg-secondary rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border-0 focus:ring-2 focus:ring-primary max-w-[120px] sm:max-w-none truncate"
                    >
                      <option value="all">All Batches</option>
                      <option value="uncategorized">Uncategorized</option>
                      {batches.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    
                    {/* Sort Options */}
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="bg-secondary rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border-0 focus:ring-2 focus:ring-primary max-w-[120px] sm:max-w-none truncate"
                    >
                      <option value="created_desc">Newest First</option>
                      <option value="created_asc">Oldest First</option>
                      <option value="name_asc">Name A-Z</option>
                      <option value="name_desc">Name Z-A</option>
                      <option value="views_desc">Most Views</option>
                      <option value="views_asc">Least Views</option>
                      <option value="updated_desc">Recently Updated</option>
                    </select>
                    
                    {/* Color Label Filter */}
                    <select
                      value={colorFilter}
                      onChange={(e) => setColorFilter(e.target.value)}
                      className="bg-secondary rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border-0 focus:ring-2 focus:ring-primary max-w-[100px] sm:max-w-none truncate"
                    >
                      <option value="all">All Colors</option>
                      <option value="red">🔴 Red</option>
                      <option value="orange">🟠 Orange</option>
                      <option value="yellow">🟡 Yellow</option>
                      <option value="green">🟢 Green</option>
                      <option value="blue">🔵 Blue</option>
                      <option value="purple">🟣 Purple</option>
                      <option value="none">⚪ No Label</option>
                    </select>
                    
                    {/* Refresh */}
                    <button
                      data-testid="refresh-tags-button"
                      onClick={loadTags}
                      className="bg-secondary hover:bg-secondary/80 rounded-full p-1.5 sm:p-2"
                    >
                      <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-8 sm:py-12">
                  <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="flex gap-6 relative">
                  {/* Folder Sidebar - Visible on medium screens and up */}
                  <div className="hidden md:block w-48 flex-shrink-0 relative z-40">
                    <div className="sticky top-32">
                      <FolderSidebar
                        folders={folders}
                        selectedFolder={folderFilter}
                        onSelectFolder={(folderId) => setFolderFilter(folderId)}
                        unorganizedCount={unorganizedCount}
                        onFoldersChange={loadFolders}
                        batches={batches}
                        batchCounts={batchStats}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        dragOverFolder={dragOverFolder}
                      />
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0 relative z-0">
                    {/* Mobile Folder Filter + New Folder Button */}
                    <div className="md:hidden mb-4 flex gap-2">
                      <select
                        value={folderFilter || 'all'}
                        onChange={(e) => setFolderFilter(e.target.value === 'all' ? null : e.target.value)}
                        className="flex-1 bg-secondary rounded-xl px-4 py-2.5 text-sm font-medium"
                      >
                        <option value="all">All Tags</option>
                        <option value="unorganized">Unorganized ({unorganizedCount})</option>
                        <optgroup label="Folders">
                          {folders.map(f => (
                            <option key={f.folder_id} value={f.folder_id}>{f.name} ({f.tag_count})</option>
                          ))}
                        </optgroup>
                        <optgroup label="Batches">
                          {batches.map(batchName => (
                            <option key={batchName} value={`batch:${batchName}`}>
                              {batchName} ({batchStats[batchName] || 0})
                            </option>
                          ))}
                        </optgroup>
                      </select>
                      <button
                        onClick={() => setShowMobileFolderModal(true)}
                        className="bg-primary text-primary-foreground rounded-xl px-3 py-2.5 flex items-center gap-1.5"
                        title="New Folder"
                        data-testid="mobile-new-folder-btn"
                      >
                        <FolderPlus className="w-4 h-4" />
                        <span className="text-sm font-medium">New</span>
                      </button>
                    </div>

                    {/* Select All + Quick Select */}
                    {tags.length > 0 && viewMode !== 'folders' && (
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedTags.length === getFilteredTags.length && getFilteredTags.length > 0}
                          onChange={() => toggleSelectAll(getFilteredTags)}
                          className="w-4 h-4 rounded border-border"
                        />
                        <span className="text-xs sm:text-sm text-muted-foreground">
                          Select all ({getFilteredTags.length})
                        </span>
                      </div>
                      <QuickSelectMenu
                        onSelectAll={selectAllFiltered}
                        onSelectWithContent={selectAllWithContent}
                        onSelectEmpty={selectAllEmpty}
                        onSelectRecent={selectRecent}
                        onSelectByFolder={selectByBatch}
                        onClearSelection={() => setSelectedTags([])}
                        folders={getAllFolders}
                        folderCounts={folderCounts}
                        counts={{ all: getFilteredTags.length, withContent: statusCounts.withContent, empty: statusCounts.empty }}
                        hasSelection={selectedTags.length > 0}
                      />
                    </div>
                  )}

                  {/* Drag hint */}
                  {viewMode !== 'folders' && !draggedTag && (
                    <p className="text-xs text-muted-foreground flex items-center gap-2">
                      <GripVertical className="w-3 h-3" />
                      Drag any card to move it to a different folder
                    </p>
                  )}

                  {/* Folder Drop Zones */}
                  {draggedTag && (
                    <div className="sticky top-20 z-20 bg-background/95 backdrop-blur-xl -mx-6 px-6 py-3 border-b border-border/40">
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {getAllFolders.map(folder => (
                          <div
                            key={folder}
                            onDragOver={(e) => handleDragOver(e, folder)}
                            onDrop={(e) => handleDrop(e, folder)}
                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                              dragOverFolder === folder
                                ? 'bg-primary text-primary-foreground scale-110 shadow-lg'
                                : 'bg-secondary/50 border-2 border-dashed border-border hover:border-primary'
                            }`}
                          >
                            <FolderOpen className="w-4 h-4 inline mr-2" />
                            {folder}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Grid View */}
                  {viewMode === 'grid' && (
                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 overflow-visible">
                      {getPaginatedTags.map((tag, index) => (
                        <TagCard
                          key={tag.nfc_code}
                          tag={tag}
                          index={index}
                          isSelected={selectedTags.includes(tag.nfc_code)}
                          isHovered={hoveredTag === tag.nfc_code}
                          isDragging={draggedTag?.nfc_code === tag.nfc_code}
                          onSelect={toggleSelectTag}
                          onHover={setHoveredTag}
                          onHoverEnd={() => setHoveredTag(null)}
                          onView={() => window.open(`/${tag.nfc_code}`, '_blank')}
                          onAnalytics={() => navigate(`/admin/analytics/${tag.nfc_code}`)}
                          onDownloadQR={() => setQrStyleModal({ open: true, nfcCode: tag.nfc_code })}
                          onCopyURL={() => copyToClipboard(`${window.location.origin}/${tag.nfc_code}`)}
                          onReset={() => setResetConfirm(tag.nfc_code)}
                          onDelete={() => setDeleteConfirm({ type: 'single', nfcCode: tag.nfc_code })}
                          onSetDates={(tag) => setDateModal({ open: true, tag })}
                          onToggleQR={handleToggleQR}
                          onSetColorLabel={setColorLabel}
                          onDragStart={handleDragStart}
                          onDragEnd={() => setDraggedTag(null)}
                          thumbnailUrl={getTagThumbnail(tag)}
                        />
                      ))}
                    </div>
                  )}

                  {/* List View */}
                  {viewMode === 'list' && (
                    <div className="rounded-xl sm:rounded-2xl border border-border/40 bg-card/50 overflow-hidden overflow-x-auto">
                      <table className="w-full min-w-[600px]">
                        <thead className="bg-secondary/30">
                          <tr>
                            <th className="p-2 sm:p-3 text-left text-xs font-medium text-muted-foreground w-8"></th>
                            <th className="p-2 sm:p-3 text-left text-xs font-medium text-muted-foreground w-8">
                              <input
                                type="checkbox"
                                checked={selectedTags.length === getFilteredTags.length && getFilteredTags.length > 0}
                                onChange={() => toggleSelectAll(getFilteredTags)}
                                className="w-4 h-4 rounded"
                              />
                            </th>
                            <th className="p-3 text-left text-xs font-medium text-muted-foreground">Title</th>
                            <th className="p-3 text-left text-xs font-medium text-muted-foreground hidden md:table-cell">Code</th>
                            <th className="p-3 text-left text-xs font-medium text-muted-foreground hidden lg:table-cell">Batch</th>
                            <th className="p-3 text-left text-xs font-medium text-muted-foreground">Files</th>
                            <th className="p-3 text-left text-xs font-medium text-muted-foreground hidden md:table-cell">Date</th>
                            <th className="p-3 text-right text-xs font-medium text-muted-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border/40">
                          {getPaginatedTags.map((tag) => (
                            <tr 
                              key={tag.nfc_code} 
                              draggable
                              onDragStart={(e) => handleDragStart(e, tag)}
                              onDragEnd={() => setDraggedTag(null)}
                              className={`hover:bg-secondary/20 cursor-grab active:cursor-grabbing transition-all ${
                                selectedTags.includes(tag.nfc_code) ? 'bg-primary/5' : ''
                              } ${draggedTag?.nfc_code === tag.nfc_code ? 'opacity-50 bg-primary/10' : ''}`}
                            >
                              <td className="p-3 text-muted-foreground"><GripVertical className="w-4 h-4" /></td>
                              <td className="p-3">
                                <input
                                  type="checkbox"
                                  checked={selectedTags.includes(tag.nfc_code)}
                                  onChange={() => toggleSelectTag(tag.nfc_code)}
                                  className="w-4 h-4 rounded"
                                />
                              </td>
                              <td className="p-3">
                                <span className="font-medium truncate block max-w-[200px]">{tag.description || 'Untitled'}</span>
                              </td>
                              <td className="p-3 hidden md:table-cell">
                                <code className="text-xs text-muted-foreground truncate block max-w-[150px]">{tag.nfc_code}</code>
                              </td>
                              <td className="p-3 hidden lg:table-cell">
                                <span className="text-xs bg-secondary px-2 py-1 rounded-full">{tag.batch || 'none'}</span>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{tag.media_files?.length || 0}</td>
                              <td className="p-3 text-xs text-muted-foreground hidden md:table-cell">
                                {new Date(tag.created_at).toLocaleDateString()}
                              </td>
                              <td className="p-3">
                                <div className="flex items-center justify-end gap-1">
                                  <button onClick={() => window.open(`/${tag.nfc_code}`, '_blank')} className="p-1.5 hover:bg-secondary rounded" title="View">
                                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                                  </button>
                                  <button onClick={() => navigate(`/admin/analytics/${tag.nfc_code}`)} className="p-1.5 hover:bg-secondary rounded" title="Analytics">
                                    <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
                                  </button>
                                  <button onClick={() => setQrStyleModal({ open: true, nfcCode: tag.nfc_code })} className="p-1.5 hover:bg-secondary rounded" title="QR Code">
                                    <QrCode className="w-4 h-4" strokeWidth={1.5} />
                                  </button>
                                  <button onClick={() => copyToClipboard(`${window.location.origin}/${tag.nfc_code}`)} className="p-1.5 hover:bg-secondary rounded" title="Copy URL">
                                    <Copy className="w-4 h-4" strokeWidth={1.5} />
                                  </button>
                                  <button onClick={() => setResetConfirm(tag.nfc_code)} className="p-1.5 hover:bg-orange-100 text-orange-600 rounded" title="Reset">
                                    <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                                  </button>
                                  <button onClick={() => setDeleteConfirm({ type: 'single', nfcCode: tag.nfc_code })} className="p-1.5 hover:bg-destructive/20 text-destructive rounded" title="Delete">
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Folder View */}
                  {viewMode === 'folders' && (
                    <div className="space-y-4">
                      {Object.entries(getTagsByFolder).map(([folder, folderTags]) => (
                        <div 
                          key={folder}
                          className={`rounded-2xl border overflow-hidden transition-all ${
                            dragOverFolder === folder
                              ? 'border-primary bg-primary/5'
                              : 'border-border/40 bg-card/50'
                          }`}
                          onDragOver={(e) => handleDragOver(e, folder)}
                          onDrop={(e) => handleDrop(e, folder)}
                        >
                          <button
                            onClick={() => setExpandedFolders(prev => ({ ...prev, [folder]: !prev[folder] }))}
                            className="w-full p-4 flex items-center justify-between hover:bg-secondary/20 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {expandedFolders[folder] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                              <FolderOpen className="w-5 h-5 text-primary" />
                              <span className="font-semibold">{folder}</span>
                              <span className="text-sm text-muted-foreground">({folderTags.length} tags)</span>
                            </div>
                          </button>
                          {expandedFolders[folder] && folderTags.length > 0 && (
                            <div className="border-t border-border/40 bg-secondary/10 p-4 grid gap-2 max-h-96 overflow-y-auto">
                              {folderTags.map(tag => (
                                <div key={tag.nfc_code} className="flex items-center justify-between bg-background/50 rounded-xl p-3">
                                  <div className="flex-1 min-w-0">
                                    <code className="text-sm font-mono truncate block">{tag.nfc_code}</code>
                                    {tag.description && <p className="text-xs text-muted-foreground truncate">{tag.description}</p>}
                                  </div>
                                  <div className="flex items-center gap-2 ml-2">
                                    <span className={`text-xs px-2 py-1 rounded-full ${tag.has_content ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                      {tag.has_content ? 'Has Content' : 'Empty'}
                                    </span>
                                    <button onClick={() => window.open(`/${tag.nfc_code}`, '_blank')} className="p-2 hover:bg-secondary rounded-full" title="View">
                                      <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => setQrStyleModal({ open: true, nfcCode: tag.nfc_code })} className="p-2 hover:bg-secondary rounded-full" title="QR Code">
                                      <QrCode className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  {viewMode !== 'folders' && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={getFilteredTags.length}
                      itemsPerPage={tagsPerPage}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setTagsPerPage}
                    />
                  )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Batches Tab */}
          {activeTab === 'batches' && (
            <BatchList
              batches={batches}
              batchStats={batchStats}
              tags={tags}
              onDownloadQR={downloadQRCode}
              onDeleteBatch={deleteBatch}
              onViewAnalytics={(nfcCode) => navigate(`/admin/analytics/${nfcCode}`)}
            />
          )}

          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <CampaignManager />
          )}

          {/* UUID Generator Tab */}
          {activeTab === 'generator' && (
            <UUIDGenerator onGenerate={handleTagsCreated} />
          )}

          {/* Custom Tag Tab */}
          {activeTab === 'custom' && (
            <CustomTagForm batches={batches} onCreate={handleTagsCreated} />
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-6">
              {/* Header with filters */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl font-bold">Contact Form Submissions</h2>
                <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
                  {[
                    { id: 'all', label: 'All', icon: MessageSquare },
                    { id: 'new', label: 'New', icon: Clock },
                    { id: 'read', label: 'Read', icon: Eye },
                    { id: 'replied', label: 'Replied', icon: CheckCircle },
                    { id: 'archived', label: 'Archived', icon: Archive },
                  ].map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => { setContactFilter(filter.id); setContactsPage(1); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                        contactFilter === filter.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary/50 text-secondary-foreground hover:bg-secondary'
                      }`}
                      data-testid={`contact-filter-${filter.id}`}
                    >
                      <filter.icon className="w-3.5 h-3.5" />
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact List */}
              {contactsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-16 bg-card rounded-2xl border border-border">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No contact submissions yet</h3>
                  <p className="text-muted-foreground">
                    When visitors submit the contact form on your website, they'll appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <motion.div
                      key={contact.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`bg-card rounded-xl border border-border p-4 sm:p-6 ${
                        contact.status === 'new' ? 'border-l-4 border-l-primary' : ''
                      }`}
                      data-testid={`contact-card-${contact.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">
                              {contact.first_name} {contact.last_name}
                            </h3>
                            {contact.status === 'new' && (
                              <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                                New
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mb-3">
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                              <Mail className="w-3.5 h-3.5" />
                              {contact.email}
                            </a>
                            {contact.company && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {contact.company}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {new Date(contact.submitted_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <p className="text-foreground whitespace-pre-wrap">{contact.message}</p>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex sm:flex-col gap-2 sm:items-end">
                          <a
                            href={`mailto:${contact.email}?subject=Re: Your inquiry to ScanIt2Go`}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                            onClick={() => updateContactStatus(contact.id, 'replied')}
                          >
                            <Mail className="w-3.5 h-3.5" />
                            Reply
                          </a>
                          <div className="flex gap-1">
                            {contact.status !== 'read' && contact.status !== 'replied' && (
                              <button
                                onClick={() => updateContactStatus(contact.id, 'read')}
                                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                                title="Mark as read"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                            {contact.status !== 'archived' && (
                              <button
                                onClick={() => updateContactStatus(contact.id, 'archived')}
                                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                                title="Archive"
                              >
                                <Archive className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                if (confirm('Delete this contact submission?')) {
                                  deleteContact(contact.id);
                                }
                              }}
                              className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Pagination */}
                  {contactsTotal > 20 && (
                    <div className="flex items-center justify-center gap-2 pt-4">
                      <button
                        onClick={() => setContactsPage(p => Math.max(1, p - 1))}
                        disabled={contactsPage === 1}
                        className="px-3 py-1.5 rounded-lg bg-secondary text-secondary-foreground disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-muted-foreground">
                        Page {contactsPage} of {Math.ceil(contactsTotal / 20)}
                      </span>
                      <button
                        onClick={() => setContactsPage(p => p + 1)}
                        disabled={contactsPage >= Math.ceil(contactsTotal / 20)}
                        className="px-3 py-1.5 rounded-lg bg-secondary text-secondary-foreground disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bulk Upload Tab */}
          {activeTab === 'bulk' && (
            <BulkUpload onUpload={handleTagsCreated} />
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold">Business Documents</h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    Download summaries, guides, and operational documents
                  </p>
                </div>
              </div>

              {/* Executive Summaries */}
              <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Executive Summaries
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'Executive Summary', file: 'ScanIt2Go_Executive_Summary', desc: 'One-page overview' },
                    { name: 'Executive Summary 2025-2027', file: 'ScanIt2Go_Investor_Executive_Summary_2025-2027', desc: 'B2B & D2C with market projections' },
                    { name: 'Combined Summary', file: 'ScanIt2Go_Combined_Summary', desc: 'Multi-business summary' },
                    { name: 'Gifts & Awards Summary', file: 'ScanIt2Go_Gifts_Awards_Summary', desc: 'D2C executive summary' },
                  ].map((doc) => (
                    <div key={doc.file} className="p-4 rounded-xl bg-secondary/30 border border-border/20">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-sm">{doc.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{doc.desc}</p>
                        </div>
                        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </div>
                      <div className="flex gap-2 mt-3">
                        <a
                          href={`/downloads/${doc.file}.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
                          data-testid={`download-${doc.file}-pdf`}
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </a>
                        <a
                          href={`/downloads/${doc.file}.docx`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                          data-testid={`download-${doc.file}-docx`}
                        >
                          <Download className="w-3 h-3" />
                          DOCX
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Operational Documents */}
              <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Operational & Compliance
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'System Architecture', file: 'ScanIt2Go-System-Architecture', desc: 'Technical architecture docs', pdfOnly: true },
                    { name: 'White-Label Onboarding', file: 'ScanIt2Go_WhiteLabel_Onboarding_Guide', desc: 'Client setup guide' },
                    { name: 'Demo Script', file: 'ScanIt2Go_Demo_Script', desc: 'Sales demonstration guide' },
                    { name: 'Privacy Policy', file: 'ScanIt2Go_Privacy_Policy', desc: 'GDPR-compliant policy' },
                    { name: 'Data Protection Guide', file: 'ScanIt2Go_Data_Protection_Guide', desc: 'Data handling procedures' },
                    { name: 'Compliance Checklist', file: 'ScanIt2Go_Compliance_Checklist', desc: 'Regulatory compliance' },
                  ].map((doc) => (
                    <div key={doc.file} className="p-4 rounded-xl bg-secondary/30 border border-border/20">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-sm">{doc.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{doc.desc}</p>
                        </div>
                        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </div>
                      <div className="flex gap-2 mt-3">
                        <a
                          href={`/downloads/${doc.file}.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
                          data-testid={`download-${doc.file}-pdf`}
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </a>
                        {!doc.pdfOnly && (
                          <a
                            href={`/downloads/${doc.file}.docx`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                            data-testid={`download-${doc.file}-docx`}
                          >
                            <Download className="w-3 h-3" />
                            DOCX
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Market Research Documents */}
              <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-500" />
                  Market Research
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'NFC & QR Market Projections', file: 'NFC_QR_Market_Projections', desc: '2025-2027 market analysis & forecasts' },
                  ].map((doc) => (
                    <div key={doc.file} className="p-4 rounded-xl bg-secondary/30 border border-border/20">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-sm">{doc.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{doc.desc}</p>
                        </div>
                        <BarChart3 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </div>
                      <div className="flex gap-2 mt-3">
                        <a
                          href={`/downloads/${doc.file}.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
                          data-testid={`download-${doc.file}-pdf`}
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </a>
                        <a
                          href={`/downloads/${doc.file}.docx`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                          data-testid={`download-${doc.file}-docx`}
                        >
                          <Download className="w-3 h-3" />
                          DOCX
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                All documents open in a new tab. Right-click and select "Save link as..." to download directly.
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Interactive Help Menu */}
      <HelpMenu availableTours={['dashboard', 'tagManagement', 'qrCodes', 'bulkOperations']} />

      {/* AI Assistant Panel */}
      <AdminAssistant isOpen={showAIAssistant} onClose={() => setShowAIAssistant(false)} />

      {/* Floating AI Assistant Button - Top Right */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowAIAssistant(!showAIAssistant)}
        className={`fixed top-24 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full shadow-lg transition-all ${
          showAIAssistant 
            ? 'bg-indigo-700 text-white' 
            : 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-xl hover:shadow-indigo-500/30'
        }`}
        style={{
          boxShadow: showAIAssistant ? 'none' : '0 0 20px rgba(99, 102, 241, 0.4)'
        }}
        title="AI Admin Assistant"
        data-testid="ai-assistant-btn"
      >
        <Bot className="w-5 h-5" />
        <span className="font-semibold">AI Admin Assistant</span>
        {!showAIAssistant && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
        )}
      </motion.button>

      {/* Version Footer */}
      <div className="fixed bottom-2 right-2 text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">
        <span title={`Build: ${BUILD_DATE}`}>v{APP_VERSION}</span>
      </div>
    </div>
  );
};

export default AdminDashboard;
